import * as i0 from '@angular/core';
import { forwardRef, EventEmitter, Component, ViewEncapsulation, ChangeDetectionStrategy, Input, Output, ViewChild, Directive, NgModule } from '@angular/core';
import { NG_VALUE_ACCESSOR, CheckboxRequiredValidator, NG_VALIDATORS } from '@angular/forms';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
const _c0 = ["inputCheckbox"];
const _c1 = ["labelWrapper"];
function CheckboxComponent_div_3_Template(rf, ctx) {
  if (rf & 1) {
    const _r4 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 4)(1, "label", 5, 6);
    i0.ɵɵlistener("click", function CheckboxComponent_div_3_Template_label_click_1_listener($event) {
      i0.ɵɵrestoreView(_r4);
      const ctx_r3 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r3.preventLabelDefaultBehavior($event));
    });
    i0.ɵɵprojection(3);
    i0.ɵɵelementEnd()();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵattribute("for", ctx_r1.id);
  }
}
const _c2 = ["*"];
let checkboxCount = 1;
/** Event object emitted by Checkbox. */
class CheckboxEvent {
  constructor(source, checked, indeterminate) {
    this.source = source;
    this.checked = checked;
    this.indeterminate = indeterminate;
  }
}
const SOL_CHECKBOX_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => CheckboxComponent),
  multi: true
};
class CheckboxComponent {
  set id(id) {
    this._id = id;
  }
  get id() {
    if (!this._id) {
      return `sol-checkbox-${this._uniqueId}`;
    }
    return this._id;
  }
  /**
   * Set the checkbox's indeterminate state to match the parameter and
   * transition the view to reflect the change.
   *
   * Allows double binding with the `indeterminateChange` Output.
   */
  set indeterminate(indeterminate) {
    if (indeterminate === this._indeterminate) {
      return;
    }
    this._indeterminate = indeterminate;
    if (this._indeterminate) {
      this.transitionCheckboxState(3 /* CheckboxState.Indeterminate */);
    } else {
      this.transitionCheckboxState(this.checked ? 2 /* CheckboxState.Checked */ : 1 /* CheckboxState.Unchecked */);
    }
    /* istanbul ignore else */
    if (this.inputCheckbox) {
      this.inputCheckbox.nativeElement.indeterminate = indeterminate;
    }
    this.changeDetectorRef.markForCheck();
    this.emitIndeterminateChangeEvent();
  }
  /**
   * Reflects whether the checkbox state is indeterminate.
   */
  get indeterminate() {
    return this._indeterminate;
  }
  /**
   * Sets the `checked` state. `true` for checked, `false` for unchecked
   *
   * Allows double binding with the `checkedChange` Output.
   */
  set checked(checked) {
    this.setChecked(checked, false);
  }
  /**
   * Returns value `true` if state is selected for the checkbox.
   */
  get checked() {
    return this._checked;
  }
  constructor(changeDetectorRef) {
    this.changeDetectorRef = changeDetectorRef;
    this.disabled = false;
    this.readOnly = false;
    this.name = '';
    this.required = false;
    this.value = '';
    this.ariaLabel = '';
    this.ariaLabelledby = null;
    this.ariaDescribedby = '';
    this.tabindex = 0;
    //Determine if the label element should be added to the component
    this.showLabel = true;
    this._id = '';
    /**
     * Emits click event.
     */
    this.clicked = new EventEmitter();
    /**
     * Emits an event when the value of the checkbox changes.
     *
     * Allows double biding with the `checked` Input.
     */
    this.checkedChange = new EventEmitter();
    /**
     * Emits event notifying other classes when a change in state occurs specifically
     * on an indeterminate checkbox.
     */
    this.indeterminateChange = new EventEmitter();
    /**
     * Blur event
     */
    this.blur = new EventEmitter();
    /**
     * Focus event
     */
    this.focus = new EventEmitter();
    /**
     * Set to `true` if the input checkbox is selected (or checked).
     */
    this._checked = false;
    /**
     * Set to `true` if the input checkbox is in state indeterminate.
     */
    this._indeterminate = false;
    /**
     * Keeps a reference to the checkboxes current state, as defined in `CheckboxState`.
     */
    this.currentCheckboxState = 0 /* CheckboxState.Init */;
    /**
     * Method set in `registerOnChange` to propagate changes back to the form.
     */
    this.propagateChange = _ => {};
    /**
     * Called when checkbox is blurred. Needed to properly implement `ControlValueAccessor`.
     */
    this._onTouched = () => {};
    this._uniqueId = checkboxCount++;
  }
  /**
   * Writes a value from `ngModel` to the component.
   *
   * In this case the value is the `checked` property.
   *
   * @param value boolean, corresponds to the `checked` property.
   */
  writeValue(value) {
    // Set checked and reset indeterminate
    this.setChecked(!!value, true);
  }
  /**
   * Sets a method in order to propagate changes back to the form.
   */
  registerOnChange(fn) {
    this.propagateChange = fn;
  }
  /**
   * Registers a callback to be triggered when the control has been touched.
   * @param fn Callback to be triggered when the checkbox is touched.
   */
  registerOnTouched(fn) {
    this._onTouched = fn;
  }
  /**
   * `ControlValueAccessor` method to programmatically disable the checkbox.
   *
   * ex: `this.formGroup.get("myCheckbox").disable();`
   *
   * @param isDisabled `true` to disable the checkbox
   */
  setDisabledState(isDisabled) {
    this.disabled = isDisabled;
    /* istanbul ignore else */
    if (this.inputCheckbox) {
      this.inputCheckbox.nativeElement.disabled = this.disabled;
    }
  }
  onBlur() {
    if (!this.readOnly) {
      this.blur.emit(this.getCheckboxEvent());
      this._onTouched();
    }
  }
  onFocus() {
    if (!this.readOnly) this.focus.emit(this.getCheckboxEvent());
  }
  /**
   * Executes on the event of a change within `Checkbox` to block propagation.
   */
  onChange(event) {
    event.stopPropagation();
  }
  /**
   * Handles click events on the `Checkbox` and emits changes to other classes.
   */
  onClick(event) {
    if (this.readOnly) {
      // Disable default checkbox activation behavior which flips checked and resets indeterminate.
      // This allows the parent component to control the checked/indeterminate properties.
      console.log(this._checked);
      event.preventDefault();
      this.clicked.emit();
      return;
    }
    event.stopPropagation();
    /* istanbul ignore else */
    if (!this.disabled) {
      this.toggle();
      this.transitionCheckboxState(this._checked ? 2 /* CheckboxState.Checked */ : 1 /* CheckboxState.Unchecked */);
      this.emitCheckedChangeEvent();
    }
    this.clicked.emit();
  }
  /**
   * Handles changes between checkbox states.
   */
  transitionCheckboxState(newState) {
    this.currentCheckboxState = newState;
  }
  /**
   * Updates the checkbox if it is in the indeterminate state.
   */
  ngAfterViewInit() {
    /* istanbul ignore else */
    if (this.inputCheckbox) {
      this.inputCheckbox.nativeElement.indeterminate = this._indeterminate;
    }
    /* istanbul ignore else */
    if (this.labelWrapper && this.labelWrapper.nativeElement) {
      this.showLabel = this.labelWrapper.nativeElement.childNodes.length > 0;
    }
    this.changeDetectorRef.detectChanges();
  }
  /**
   * Toggle the selected state of the checkbox.
   */
  toggle() {
    // Flip checked and reset indeterminate
    this.setChecked(!this.checked, true);
    this.propagateChange(this._checked);
  }
  preventLabelDefaultBehavior(event) {
    // prevent the default behavior of label during readonly mode
    // but emit clicked event but not in disabled state.
    // If pointer events in none for disabled state
    // then we can use only `this.readonly` in if clause
    if (this.readOnly && !this.disabled) {
      event.preventDefault();
      this.clicked.emit();
    }
  }
  /**
   * Sets checked state and optionally resets indeterminate state.
   */
  setChecked(checked, resetIndeterminate) {
    if (checked === this._checked) {
      return;
    }
    this._checked = checked;
    // Reset indeterminate if requested
    if (resetIndeterminate && this._indeterminate) {
      /* istanbul ignore else */
      if (this.indeterminate && this.inputCheckbox) {
        this.inputCheckbox.nativeElement.indeterminate = false;
        this.inputCheckbox.nativeElement.checked = this._checked;
      }
      this._indeterminate = false;
      this.transitionCheckboxState(this._checked ? 2 /* CheckboxState.Checked */ : 1 /* CheckboxState.Unchecked */);
      this.emitIndeterminateChangeEvent();
    } else if (this.currentCheckboxState !== 3 /* CheckboxState.Indeterminate */) {
      this.transitionCheckboxState(this._checked ? 2 /* CheckboxState.Checked */ : 1 /* CheckboxState.Unchecked */);
    }
    this.changeDetectorRef.markForCheck();
  }
  emitCheckedChangeEvent() {
    this.checkedChange.emit(this.getCheckboxEvent());
  }
  emitIndeterminateChangeEvent() {
    this.indeterminateChange.emit(this.getCheckboxEvent());
  }
  getCheckboxEvent() {
    return new CheckboxEvent(this, this.checked, this.indeterminate);
  }
  static {
    this.ɵfac = function CheckboxComponent_Factory(t) {
      return new (t || CheckboxComponent)(i0.ɵɵdirectiveInject(i0.ChangeDetectorRef));
    };
  }
  static {
    this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: CheckboxComponent,
      selectors: [["sol-checkbox"]],
      viewQuery: function CheckboxComponent_Query(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵviewQuery(_c0, 5);
          i0.ɵɵviewQuery(_c1, 5);
        }
        if (rf & 2) {
          let _t;
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.inputCheckbox = _t.first);
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.labelWrapper = _t.first);
        }
      },
      inputs: {
        disabled: "disabled",
        readOnly: "readOnly",
        name: "name",
        required: "required",
        value: "value",
        ariaLabel: "ariaLabel",
        ariaLabelledby: "ariaLabelledby",
        ariaDescribedby: "ariaDescribedby",
        tabindex: "tabindex",
        id: "id",
        indeterminate: "indeterminate",
        checked: "checked"
      },
      outputs: {
        clicked: "clicked",
        checkedChange: "checkedChange",
        indeterminateChange: "indeterminateChange",
        blur: "blur",
        focus: "focus"
      },
      features: [i0.ɵɵProvidersFeature([SOL_CHECKBOX_VALUE_ACCESSOR])],
      ngContentSelectors: _c2,
      decls: 4,
      vars: 14,
      consts: [[1, "sol-checkbox"], ["type", "checkbox", 1, "sol-checkbox-inputbox", 3, "tabIndex", "id", "required", "checked", "disabled", "change", "click", "focus", "blur"], ["inputCheckbox", ""], ["class", "sol-checkbox-label-text", 4, "ngIf"], [1, "sol-checkbox-label-text"], [3, "click"], ["labelWrapper", ""]],
      template: function CheckboxComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵprojectionDef();
          i0.ɵɵelementStart(0, "div", 0)(1, "input", 1, 2);
          i0.ɵɵlistener("change", function CheckboxComponent_Template_input_change_1_listener($event) {
            return ctx.onChange($event);
          })("click", function CheckboxComponent_Template_input_click_1_listener($event) {
            return ctx.onClick($event);
          })("focus", function CheckboxComponent_Template_input_focus_1_listener() {
            return ctx.onFocus();
          })("blur", function CheckboxComponent_Template_input_blur_1_listener() {
            return ctx.onBlur();
          });
          i0.ɵɵelementEnd();
          i0.ɵɵtemplate(3, CheckboxComponent_div_3_Template, 4, 1, "div", 3);
          i0.ɵɵelementEnd();
        }
        if (rf & 2) {
          i0.ɵɵadvance();
          i0.ɵɵclassProp("readonly", ctx.readOnly && !ctx.disabled);
          i0.ɵɵproperty("tabIndex", ctx.readOnly ? -1 : ctx.tabindex)("id", ctx.id)("required", ctx.required)("checked", ctx.checked)("disabled", ctx.disabled);
          i0.ɵɵattribute("value", ctx.value)("name", ctx.name)("aria-label", ctx.ariaLabel || null)("aria-labelledby", ctx.ariaLabelledby)("aria-describedby", ctx.ariaDescribedby || null)("aria-checked", ctx.indeterminate ? "mixed" : ctx.checked);
          i0.ɵɵadvance(2);
          i0.ɵɵproperty("ngIf", ctx.showLabel);
        }
      },
      dependencies: [i1.NgIf],
      styles: [".sol-checkbox{display:inline-flex;flex-direction:row;align-items:center;min-height:1.5rem}.sol-checkbox .sol-checkbox-label-text label{display:block;color:var(--color-interface-text700);font-family:var(--typography-opensans-body-body1-font-family);font-style:var(--typography-opensans-body-body1-font-style);font-weight:var(--typography-opensans-body-body1-font-weight);font-size:var(--typography-opensans-body-body1-font-size);line-height:var(--typography-opensans-body-body1-line-height);text-decoration:var(--typography-opensans-body-body1-text-decoration);letter-spacing:var(--typography-opensans-body-body1-letter-spacing);margin:0 0 0 .5rem;cursor:pointer}.sol-checkbox input.sol-checkbox-inputbox{box-sizing:border-box;position:relative;min-width:1rem;width:1rem;height:1rem;border-radius:var(--radii-small-allcorners);border-width:var(--border-size1);border-style:solid;cursor:pointer;display:inline-flex;justify-content:center;align-items:center;margin:auto;appearance:none;background:#fff;border-color:var(--color-globalstate-active-border)}.sol-checkbox input.sol-checkbox-inputbox:focus{outline:none}.sol-checkbox input.sol-checkbox-inputbox:focus-visible{outline:var(--color-globalstate-focus) solid var(--border-size1);outline-offset:calc(var(--outline-offset4) - var(--border-size1));background:var(--color-interface-background125)}.sol-checkbox input.sol-checkbox-inputbox:hover{background:var(--color-interface-background125)}.sol-checkbox input.sol-checkbox-inputbox:active{background:var(--color-globalstate-inactive-border)}.sol-checkbox input.sol-checkbox-inputbox:disabled{cursor:default;pointer-events:none;background:var(--color-globalstate-disabled-background);border-color:var(--color-globalstate-disabled-borderalt)}.sol-checkbox input.sol-checkbox-inputbox:disabled+.sol-checkbox-label-text{color:var(--color-globalstate-disabled-content)}.sol-checkbox input.sol-checkbox-inputbox:disabled+.sol-checkbox-label-text label{cursor:default;pointer-events:none}.sol-checkbox input.sol-checkbox-inputbox:checked:after{position:absolute;width:.5rem;height:.5rem;content:\"\";background-size:contain;background-position:center;background-repeat:no-repeat;background-image:url(\"data:image/svg+xml,%3Csvg width='8' height='6' viewBox='0 0 8 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M3.54986 5.62942C3.18775 5.97919 2.61362 5.97919 2.25151 5.62942L0.285241 3.73019C-0.0859441 3.37165 -0.0962022 2.7801 0.262329 2.40892V2.40892C0.62086 2.03773 1.21241 2.02747 1.5836 2.38601L2.90068 3.65819L6.4164 0.262329C6.78759 -0.0962022 7.37914 -0.0859441 7.73767 0.285241V0.285241C8.0962 0.656426 8.08594 1.24798 7.71476 1.60651L3.54986 5.62942Z' fill='white'/%3E%3C/svg%3E\")}.sol-checkbox input.sol-checkbox-inputbox:indeterminate:after{position:absolute;width:.5rem;height:.5rem;content:\"\";background-size:contain;background-position:center;background-repeat:no-repeat;background-image:url(\"data:image/svg+xml,%3Csvg width='8' height='2' viewBox='0 0 8 2' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='8' height='2' rx='1' fill='white'/%3E%3C/svg%3E\")}.sol-checkbox input.sol-checkbox-inputbox:indeterminate,.sol-checkbox input.sol-checkbox-inputbox:checked{background:var(--color-interface-blue450);border-color:var(--color-interface-blue450)}.sol-checkbox input.sol-checkbox-inputbox:indeterminate:focus-visible,.sol-checkbox input.sol-checkbox-inputbox:checked:focus-visible{background:var(--color-interface-blue550);border-color:var(--color-interface-blue550)}.sol-checkbox input.sol-checkbox-inputbox:indeterminate:hover,.sol-checkbox input.sol-checkbox-inputbox:checked:hover{background:var(--color-interface-blue550);border-color:var(--color-interface-blue550)}.sol-checkbox input.sol-checkbox-inputbox:indeterminate:active,.sol-checkbox input.sol-checkbox-inputbox:checked:active{background:var(--color-interface-blue650);border-color:var(--color-interface-blue650)}.sol-checkbox input.sol-checkbox-inputbox:indeterminate:disabled,.sol-checkbox input.sol-checkbox-inputbox:checked:disabled{background:var(--color-globalstate-disabled-border);border-color:var(--color-globalstate-disabled-border)}.sol-checkbox input.sol-checkbox-inputbox.readonly{background:var(--color-globalstate-disabled-background);border-color:var(--color-globalstate-disabled-border)}.sol-checkbox input.sol-checkbox-inputbox.readonly:checked:after{position:absolute;width:.5rem;height:.5rem;content:\"\";background-size:contain;background-position:center;background-repeat:no-repeat;background-image:url(\"data:image/svg+xml,%3Csvg width='8' height='6' viewBox='0 0 8 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M3.54986 5.62942C3.18775 5.97919 2.61362 5.97919 2.25151 5.62942L0.285241 3.73019C-0.0859441 3.37165 -0.0962022 2.7801 0.262329 2.40892V2.40892C0.62086 2.03773 1.21241 2.02747 1.5836 2.38601L2.90068 3.65819L6.4164 0.262329C6.78759 -0.0962022 7.37914 -0.0859441 7.73767 0.285241V0.285241C8.0962 0.656426 8.08594 1.24798 7.71476 1.60651L3.54986 5.62942Z' fill='%238E9CAB'/%3E%3C/svg%3E\")}.sol-checkbox input.sol-checkbox-inputbox.readonly:indeterminate:after{position:absolute;width:.5rem;height:.5rem;content:\"\";background-size:contain;background-position:center;background-repeat:no-repeat;background-image:url(\"data:image/svg+xml,%3Csvg width='8' height='2' viewBox='0 0 8 2' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='8' height='2' rx='1' fill='%238E9CAB'/%3E%3C/svg%3E\")}.sol-checkbox input.sol-checkbox-inputbox.readonly:indeterminate,.sol-checkbox input.sol-checkbox-inputbox.readonly:checked{background:var(--color-globalstate-disabled-background);border-color:var(--color-globalstate-disabled-border)}.sol-checkbox input.sol-checkbox-inputbox.readonly:indeterminate:focus-visible,.sol-checkbox input.sol-checkbox-inputbox.readonly:checked:focus-visible{background:var(--color-globalstate-disabled-background);border-color:var(--color-globalstate-disabled-border)}.sol-checkbox input.sol-checkbox-inputbox.readonly:indeterminate:hover,.sol-checkbox input.sol-checkbox-inputbox.readonly:checked:hover{background:var(--color-globalstate-disabled-background);border-color:var(--color-globalstate-disabled-border)}.sol-checkbox input.sol-checkbox-inputbox.readonly:indeterminate:active,.sol-checkbox input.sol-checkbox-inputbox.readonly:checked:active{background:var(--color-globalstate-disabled-background);border-color:var(--color-globalstate-disabled-border)}.sol-checkbox input.sol-checkbox-inputbox.readonly:indeterminate:disabled,.sol-checkbox input.sol-checkbox-inputbox.readonly:checked:disabled{background:var(--color-globalstate-disabled-border);border-color:var(--color-globalstate-disabled-border)}\n"],
      encapsulation: 2,
      changeDetection: 0
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CheckboxComponent, [{
    type: Component,
    args: [{
      selector: 'sol-checkbox',
      providers: [SOL_CHECKBOX_VALUE_ACCESSOR],
      encapsulation: ViewEncapsulation.None,
      changeDetection: ChangeDetectionStrategy.OnPush,
      template: "<div class=\"sol-checkbox\">\n  <input\n    class=\"sol-checkbox-inputbox\"\n    [class.readonly]=\"readOnly && !disabled\"\n    [tabIndex]=\"readOnly ? -1 : tabindex\"\n    #inputCheckbox\n    type=\"checkbox\"\n    [id]=\"id\"\n    [attr.value]=\"value\"\n    [attr.name]=\"name\"\n    [required]=\"required\"\n    [checked]=\"checked\"\n    [disabled]=\"disabled\"\n    [attr.aria-label]=\"ariaLabel || null\"\n    [attr.aria-labelledby]=\"ariaLabelledby\"\n    [attr.aria-describedby]=\"ariaDescribedby || null\"\n    [attr.aria-checked]=\"indeterminate ? 'mixed' : checked\"\n    (change)=\"onChange($event)\"\n    (click)=\"onClick($event)\"\n    (focus)=\"onFocus()\"\n    (blur)=\"onBlur()\"\n  />\n\n  <div *ngIf=\"showLabel\" class=\"sol-checkbox-label-text\">\n    <label [attr.for]=\"id\" #labelWrapper (click)=\"preventLabelDefaultBehavior($event)\">\n      <ng-content></ng-content>\n    </label>\n  </div>\n</div>\n",
      styles: [".sol-checkbox{display:inline-flex;flex-direction:row;align-items:center;min-height:1.5rem}.sol-checkbox .sol-checkbox-label-text label{display:block;color:var(--color-interface-text700);font-family:var(--typography-opensans-body-body1-font-family);font-style:var(--typography-opensans-body-body1-font-style);font-weight:var(--typography-opensans-body-body1-font-weight);font-size:var(--typography-opensans-body-body1-font-size);line-height:var(--typography-opensans-body-body1-line-height);text-decoration:var(--typography-opensans-body-body1-text-decoration);letter-spacing:var(--typography-opensans-body-body1-letter-spacing);margin:0 0 0 .5rem;cursor:pointer}.sol-checkbox input.sol-checkbox-inputbox{box-sizing:border-box;position:relative;min-width:1rem;width:1rem;height:1rem;border-radius:var(--radii-small-allcorners);border-width:var(--border-size1);border-style:solid;cursor:pointer;display:inline-flex;justify-content:center;align-items:center;margin:auto;appearance:none;background:#fff;border-color:var(--color-globalstate-active-border)}.sol-checkbox input.sol-checkbox-inputbox:focus{outline:none}.sol-checkbox input.sol-checkbox-inputbox:focus-visible{outline:var(--color-globalstate-focus) solid var(--border-size1);outline-offset:calc(var(--outline-offset4) - var(--border-size1));background:var(--color-interface-background125)}.sol-checkbox input.sol-checkbox-inputbox:hover{background:var(--color-interface-background125)}.sol-checkbox input.sol-checkbox-inputbox:active{background:var(--color-globalstate-inactive-border)}.sol-checkbox input.sol-checkbox-inputbox:disabled{cursor:default;pointer-events:none;background:var(--color-globalstate-disabled-background);border-color:var(--color-globalstate-disabled-borderalt)}.sol-checkbox input.sol-checkbox-inputbox:disabled+.sol-checkbox-label-text{color:var(--color-globalstate-disabled-content)}.sol-checkbox input.sol-checkbox-inputbox:disabled+.sol-checkbox-label-text label{cursor:default;pointer-events:none}.sol-checkbox input.sol-checkbox-inputbox:checked:after{position:absolute;width:.5rem;height:.5rem;content:\"\";background-size:contain;background-position:center;background-repeat:no-repeat;background-image:url(\"data:image/svg+xml,%3Csvg width='8' height='6' viewBox='0 0 8 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M3.54986 5.62942C3.18775 5.97919 2.61362 5.97919 2.25151 5.62942L0.285241 3.73019C-0.0859441 3.37165 -0.0962022 2.7801 0.262329 2.40892V2.40892C0.62086 2.03773 1.21241 2.02747 1.5836 2.38601L2.90068 3.65819L6.4164 0.262329C6.78759 -0.0962022 7.37914 -0.0859441 7.73767 0.285241V0.285241C8.0962 0.656426 8.08594 1.24798 7.71476 1.60651L3.54986 5.62942Z' fill='white'/%3E%3C/svg%3E\")}.sol-checkbox input.sol-checkbox-inputbox:indeterminate:after{position:absolute;width:.5rem;height:.5rem;content:\"\";background-size:contain;background-position:center;background-repeat:no-repeat;background-image:url(\"data:image/svg+xml,%3Csvg width='8' height='2' viewBox='0 0 8 2' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='8' height='2' rx='1' fill='white'/%3E%3C/svg%3E\")}.sol-checkbox input.sol-checkbox-inputbox:indeterminate,.sol-checkbox input.sol-checkbox-inputbox:checked{background:var(--color-interface-blue450);border-color:var(--color-interface-blue450)}.sol-checkbox input.sol-checkbox-inputbox:indeterminate:focus-visible,.sol-checkbox input.sol-checkbox-inputbox:checked:focus-visible{background:var(--color-interface-blue550);border-color:var(--color-interface-blue550)}.sol-checkbox input.sol-checkbox-inputbox:indeterminate:hover,.sol-checkbox input.sol-checkbox-inputbox:checked:hover{background:var(--color-interface-blue550);border-color:var(--color-interface-blue550)}.sol-checkbox input.sol-checkbox-inputbox:indeterminate:active,.sol-checkbox input.sol-checkbox-inputbox:checked:active{background:var(--color-interface-blue650);border-color:var(--color-interface-blue650)}.sol-checkbox input.sol-checkbox-inputbox:indeterminate:disabled,.sol-checkbox input.sol-checkbox-inputbox:checked:disabled{background:var(--color-globalstate-disabled-border);border-color:var(--color-globalstate-disabled-border)}.sol-checkbox input.sol-checkbox-inputbox.readonly{background:var(--color-globalstate-disabled-background);border-color:var(--color-globalstate-disabled-border)}.sol-checkbox input.sol-checkbox-inputbox.readonly:checked:after{position:absolute;width:.5rem;height:.5rem;content:\"\";background-size:contain;background-position:center;background-repeat:no-repeat;background-image:url(\"data:image/svg+xml,%3Csvg width='8' height='6' viewBox='0 0 8 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M3.54986 5.62942C3.18775 5.97919 2.61362 5.97919 2.25151 5.62942L0.285241 3.73019C-0.0859441 3.37165 -0.0962022 2.7801 0.262329 2.40892V2.40892C0.62086 2.03773 1.21241 2.02747 1.5836 2.38601L2.90068 3.65819L6.4164 0.262329C6.78759 -0.0962022 7.37914 -0.0859441 7.73767 0.285241V0.285241C8.0962 0.656426 8.08594 1.24798 7.71476 1.60651L3.54986 5.62942Z' fill='%238E9CAB'/%3E%3C/svg%3E\")}.sol-checkbox input.sol-checkbox-inputbox.readonly:indeterminate:after{position:absolute;width:.5rem;height:.5rem;content:\"\";background-size:contain;background-position:center;background-repeat:no-repeat;background-image:url(\"data:image/svg+xml,%3Csvg width='8' height='2' viewBox='0 0 8 2' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='8' height='2' rx='1' fill='%238E9CAB'/%3E%3C/svg%3E\")}.sol-checkbox input.sol-checkbox-inputbox.readonly:indeterminate,.sol-checkbox input.sol-checkbox-inputbox.readonly:checked{background:var(--color-globalstate-disabled-background);border-color:var(--color-globalstate-disabled-border)}.sol-checkbox input.sol-checkbox-inputbox.readonly:indeterminate:focus-visible,.sol-checkbox input.sol-checkbox-inputbox.readonly:checked:focus-visible{background:var(--color-globalstate-disabled-background);border-color:var(--color-globalstate-disabled-border)}.sol-checkbox input.sol-checkbox-inputbox.readonly:indeterminate:hover,.sol-checkbox input.sol-checkbox-inputbox.readonly:checked:hover{background:var(--color-globalstate-disabled-background);border-color:var(--color-globalstate-disabled-border)}.sol-checkbox input.sol-checkbox-inputbox.readonly:indeterminate:active,.sol-checkbox input.sol-checkbox-inputbox.readonly:checked:active{background:var(--color-globalstate-disabled-background);border-color:var(--color-globalstate-disabled-border)}.sol-checkbox input.sol-checkbox-inputbox.readonly:indeterminate:disabled,.sol-checkbox input.sol-checkbox-inputbox.readonly:checked:disabled{background:var(--color-globalstate-disabled-border);border-color:var(--color-globalstate-disabled-border)}\n"]
    }]
  }], () => [{
    type: i0.ChangeDetectorRef
  }], {
    disabled: [{
      type: Input
    }],
    readOnly: [{
      type: Input
    }],
    name: [{
      type: Input
    }],
    required: [{
      type: Input
    }],
    value: [{
      type: Input
    }],
    ariaLabel: [{
      type: Input
    }],
    ariaLabelledby: [{
      type: Input
    }],
    ariaDescribedby: [{
      type: Input
    }],
    tabindex: [{
      type: Input
    }],
    id: [{
      type: Input
    }],
    indeterminate: [{
      type: Input
    }],
    checked: [{
      type: Input
    }],
    clicked: [{
      type: Output
    }],
    checkedChange: [{
      type: Output
    }],
    indeterminateChange: [{
      type: Output
    }],
    blur: [{
      type: Output
    }],
    focus: [{
      type: Output
    }],
    inputCheckbox: [{
      type: ViewChild,
      args: ['inputCheckbox', {
        static: false
      }]
    }],
    labelWrapper: [{
      type: ViewChild,
      args: ['labelWrapper']
    }]
  });
})();

/**
 * Validator for SOL checkbox's required attribute in template-driven checkbox.
 * Current CheckboxRequiredValidator only work with `input type=checkbox` and does not
 * work with `sol-checkbox`.
 */
class SolCheckboxRequiredValidator extends CheckboxRequiredValidator {
  static {
    this.ɵfac = /* @__PURE__ */(() => {
      let ɵSolCheckboxRequiredValidator_BaseFactory;
      return function SolCheckboxRequiredValidator_Factory(t) {
        return (ɵSolCheckboxRequiredValidator_BaseFactory || (ɵSolCheckboxRequiredValidator_BaseFactory = i0.ɵɵgetInheritedFactory(SolCheckboxRequiredValidator)))(t || SolCheckboxRequiredValidator);
      };
    })();
  }
  static {
    this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: SolCheckboxRequiredValidator,
      selectors: [["sol-checkbox", "required", "", "formControlName", ""], ["sol-checkbox", "required", "", "formControl", ""], ["sol-checkbox", "required", "", "ngModel", ""]],
      features: [i0.ɵɵProvidersFeature([{
        provide: NG_VALIDATORS,
        useExisting: forwardRef(() => SolCheckboxRequiredValidator),
        multi: true
      }]), i0.ɵɵInheritDefinitionFeature]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SolCheckboxRequiredValidator, [{
    type: Directive,
    args: [{
      selector: 'sol-checkbox[required][formControlName],sol-checkbox[required][formControl],sol-checkbox[required][ngModel]',
      providers: [{
        provide: NG_VALIDATORS,
        useExisting: forwardRef(() => SolCheckboxRequiredValidator),
        multi: true
      }]
    }]
  }], null, null);
})();

// modules
class CheckboxModule {
  static {
    this.ɵfac = function CheckboxModule_Factory(t) {
      return new (t || CheckboxModule)();
    };
  }
  static {
    this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: CheckboxModule
    });
  }
  static {
    this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      imports: [CommonModule]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CheckboxModule, [{
    type: NgModule,
    args: [{
      declarations: [CheckboxComponent, SolCheckboxRequiredValidator],
      exports: [CheckboxComponent, SolCheckboxRequiredValidator],
      imports: [CommonModule]
    }]
  }], null, null);
})();

/*
 * Public API Surface of checkbox
 */

/**
 * Generated bundle index. Do not edit.
 */

export { CheckboxComponent, CheckboxEvent, CheckboxModule, SolCheckboxRequiredValidator };
